import {
  CLASSIFICATION_LABEL,
  ENTITY_STATUS_LABEL,
  HYPOTHESIS_STATUS_LABEL,
  PRIORITY_LABEL,
  RELATION_STATUS_LABEL,
  TARGET_STATUS_LABEL,
  TIMELINE_KIND_LABEL,
  type Engagement,
  type Entity,
  type Evidence,
  type Hypothesis,
  type Relation,
  type Stats,
  type Target,
  type TimelineEvent,
} from '../state/domain';

export type CaseSnapshot = {
  engagements: Engagement[];
  activeEngagementId: string | null;
  targets: Target[];
  entities: Entity[];
  relations: Relation[];
  evidence: Evidence[];
  hypotheses: Hypothesis[];
  timeline: TimelineEvent[];
  stats: Stats | null;
};

/** 生成完整卷宗 JSON（可直接归档或迁移）。 */
export function buildCaseBundle(snapshot: CaseSnapshot): string {
  const engagement =
    snapshot.engagements.find(e => e.id === snapshot.activeEngagementId) ?? null;
  return JSON.stringify(
    {
      format: 'expotar.case.v1',
      exportedAt: new Date().toISOString(),
      engagement,
      statistics: snapshot.stats,
      targets: snapshot.targets,
      entities: snapshot.entities,
      relations: snapshot.relations,
      evidence: snapshot.evidence,
      hypotheses: snapshot.hypotheses,
      timeline: snapshot.timeline,
    },
    null,
    2
  );
}

function relName(relations: Relation[], entities: Entity[], id: string): string {
  const entity = entities.find(e => e.id === id);
  if (entity) return entity.value ?? entity.label ?? id.slice(0, 8);
  return id.slice(0, 8);
}

/** 生成案情报告（Markdown）。 */
export function buildCaseReport(snapshot: CaseSnapshot): string {
  const engagement =
    snapshot.engagements.find(e => e.id === snapshot.activeEngagementId) ?? null;
  const lines: string[] = [];
  const stamp = new Date().toLocaleString('zh-CN', { hour12: false });

  lines.push(`# 案情报告${engagement ? `：${engagement.name}` : ''}`);
  lines.push('');
  lines.push(`> 生成时间：${stamp}`);
  lines.push('');

  // 案件信息
  if (engagement) {
    lines.push('## 一、案件信息');
    lines.push('');
    lines.push('| 项目 | 内容 |');
    lines.push('| --- | --- |');
    lines.push(`| 案号 | ${engagement.caseNo ?? '—'} |`);
    lines.push(`| 密级 | ${CLASSIFICATION_LABEL[engagement.classification]} |`);
    lines.push(`| 优先级 | ${PRIORITY_LABEL[engagement.priority]} |`);
    lines.push(`| 委托方 | ${engagement.client ?? '—'} |`);
    lines.push(`| 主办人 | ${engagement.lead ?? '—'} |`);
    lines.push(`| 授权依据 | ${engagement.legalBasis ?? '—'} |`);
    lines.push(`| 授权人 | ${engagement.authorizedBy ?? '—'} |`);
    lines.push(
      `| 有效期 | ${engagement.authorizedAt ? new Date(engagement.authorizedAt).toLocaleDateString() : '—'} ~ ${
        engagement.expiresAt ? new Date(engagement.expiresAt).toLocaleDateString() : '—'
      } |`
    );
    lines.push(`| 状态 | ${engagement.status} |`);
    lines.push('');
    lines.push(`**分析目的**：${engagement.purpose ?? '—'}`);
    lines.push('');
    const scope = engagement.scope ?? {};
    lines.push('**检索范围**：');
    lines.push('');
    lines.push(`- 域名：${(scope.domains ?? []).join('、') || '—'}`);
    lines.push(`- IP：${(scope.ips ?? []).join('、') || '—'}`);
    lines.push(`- 邮箱：${(scope.emails ?? []).join('、') || '—'}`);
    lines.push(`- 组织/人员：${(scope.orgs ?? []).join('、') || '—'}`);
    lines.push('');
  }

  // 对象
  lines.push('## 二、对象档案');
  lines.push('');
  if (snapshot.targets.length === 0) lines.push('_无_');
  for (const target of snapshot.targets) {
    lines.push(`### ${target.name}${target.codename ? `（代号 ${target.codename}）` : ''}`);
    lines.push('');
    lines.push(
      `- 类型：${target.kind === 'organization' ? '组织' : '个人'}｜风险：**${target.riskScore}（${target.riskLevel}）**｜状态：${
        TARGET_STATUS_LABEL[target.status]
      }`
    );
    lines.push(
      `- 职务/组织：${[target.role, target.org].filter(Boolean).join(' / ') || '—'}｜所在地：${target.location ?? '—'}｜国籍：${
        target.nationality ?? '—'
      }`
    );
    if (target.aliases.length) lines.push(`- 别名：${target.aliases.join('、')}`);
    if (target.affiliations.length) lines.push(`- 所属机构：${target.affiliations.join('、')}`);
    if (Object.keys(target.idNumbers).length)
      lines.push(
        `- 证件/编号：${Object.entries(target.idNumbers)
          .map(([k, v]) => `${k}=${v}`)
          .join('、')}`
      );
    if (target.summary) {
      lines.push('');
      lines.push(`> ${target.summary}`);
    }
    lines.push('');
  }

  // 线索
  lines.push('## 三、线索台账');
  lines.push('');
  lines.push('| 类型 | 值 | 置信度 | 状态 | 来源 |');
  lines.push('| --- | --- | --- | --- | --- |');
  for (const entity of snapshot.entities) {
    lines.push(
      `| ${entity.type} | \`${entity.value ?? entity.label ?? ''}\` | ${Math.round(
        entity.confidence * 100
      )}% | ${ENTITY_STATUS_LABEL[entity.status]} | ${entity.source ?? '—'} |`
    );
  }
  if (snapshot.entities.length === 0) lines.push('| _无_ | | | | |');
  lines.push('');

  // 关系
  lines.push('## 四、关联关系');
  lines.push('');
  if (snapshot.relations.length === 0) lines.push('_无_');
  for (const relation of snapshot.relations) {
    lines.push(
      `- \`${relName(snapshot.relations, snapshot.entities, relation.sourceId)}\` —**${
        relation.label ?? relation.type
      }**→ \`${relName(snapshot.relations, snapshot.entities, relation.targetId)}\`（强度 ${relation.strength}，${
        RELATION_STATUS_LABEL[relation.status]
      }）`
    );
  }
  lines.push('');

  // 证据
  lines.push('## 五、证据链（Admiralty 分级）');
  lines.push('');
  if (snapshot.evidence.length === 0) lines.push('_无_');
  for (const item of snapshot.evidence) {
    lines.push(`- **[${item.reliability}]** ${item.title ?? item.url ?? '(无标题)'}（${item.kind}）`);
    if (item.seizedAt || item.location || item.witness) {
      lines.push(
        `  - 提取：${item.seizedAt ? new Date(item.seizedAt).toLocaleString('zh-CN') : '—'}｜地点：${
          item.location ?? '—'
        }｜见证人：${item.witness ?? '—'}`
      );
    }
    if (item.custody.length) {
      lines.push(`  - 保管链：${item.custody.map(c => `${c.action}(${c.actor})`).join(' → ')}`);
    }
    if (item.content) {
      const excerpt = item.content.replace(/\s+/g, ' ').slice(0, 160);
      lines.push(`  - 摘要：${excerpt}${item.content.length > 160 ? '…' : ''}`);
    }
  }
  lines.push('');

  // 时间线
  lines.push('## 六、事件时间线');
  lines.push('');
  if (snapshot.timeline.length === 0) lines.push('_无_');
  for (const event of snapshot.timeline) {
    lines.push(
      `- **${new Date(event.occurredAt).toLocaleString('zh-CN')}**　[${TIMELINE_KIND_LABEL[event.kind]}] ${
        event.title
      }${event.location ? `　@${event.location}` : ''}`
    );
    if (event.description) lines.push(`  - ${event.description}`);
  }
  lines.push('');

  // 研判
  lines.push('## 七、研判结论');
  lines.push('');
  if (snapshot.hypotheses.length === 0) lines.push('_无_');
  for (const hypothesis of snapshot.hypotheses) {
    lines.push(
      `- **${HYPOTHESIS_STATUS_LABEL[hypothesis.status]}**（置信度 ${Math.round(
        hypothesis.confidence * 100
      )}%，${hypothesis.createdBy === 'ai' ? '系统' : '人工'}）${hypothesis.statement}`
    );
    if (hypothesis.rationale) lines.push(`  - 依据：${hypothesis.rationale}`);
    if (hypothesis.alternatives.length)
      lines.push(`  - 备择解释：${hypothesis.alternatives.join('；')}`);
    if (hypothesis.supporting.length || hypothesis.contradicting.length)
      lines.push(
        `  - 证据：支持 ${hypothesis.supporting.length} 条，反证 ${hypothesis.contradicting.length} 条`
      );
  }
  lines.push('');

  // 统计
  if (snapshot.stats) {
    lines.push('## 八、统计');
    lines.push('');
    lines.push(
      `对象 ${snapshot.stats.targets}｜线索 ${snapshot.stats.entities}｜关系 ${snapshot.stats.relations}｜证据 ${snapshot.stats.evidence}｜研判 ${snapshot.stats.hypotheses}`
    );
    lines.push('');
  }

  lines.push('---');
  lines.push('');
  lines.push(
    '_本报告由 EXPOTAR 生成，仅基于授权范围内的公开信息与内部登记数据，仅供合法防御性评估使用。_'
  );

  return lines.join('\n');
}

/** 触发浏览器下载。 */
export function downloadText(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}